/**
 * Cart Drawer — Slideout Cart
 * ===========================
 * Custom element that manages a slideout cart drawer.
 *
 * Events listened:
 *   - 'cart:open'    → Opens the drawer (dispatched by pet-bundle-builder after add-to-cart)
 *   - 'cart:updated' → Re-fetches cart if drawer is already open
 *   - Click on a[href="/cart"] → Intercepts to open drawer instead of navigating
 *
 * Cart API endpoints used:
 *   - GET  /cart.js        → Fetch full cart data
 *   - POST /cart/change.js → Update item quantity or remove items
 *
 * To adjust reward thresholds and labels, edit the section settings
 * in sections/cart-drawer.liquid — no JS changes needed.
 */

if (!customElements.get('cart-drawer-custom')) {
  class CartDrawer extends HTMLElement {
    /* ------------------------------------------------------------------ */
    /*  Lifecycle                                                          */
    /* ------------------------------------------------------------------ */

    connectedCallback() {
      this._isOpen = false;
      this._isUpdating = false;
      this._cartData = null;
      this._skipNextUpdate = false;
      this._triggerElement = null;
      this._isSyncingGift = false;

      // Cache DOM refs
      this._overlay = this.querySelector('.cart-drawer__overlay');
      this._panel = this.querySelector('.cart-drawer__panel');
      this._itemsContainer = this.querySelector('.cart-drawer__items');
      this._emptyState = this.querySelector('.cart-drawer__empty');
      this._footer = this.querySelector('.cart-drawer__footer');
      this._countEl = this.querySelector('.cart-drawer__count');
      this._subtotalPrice = this.querySelector('.cart-drawer__subtotal-price');
      this._rewardsCopy = this.querySelector('.cart-drawer__rewards-copy');
      this._rewardsFill = this.querySelector('.cart-drawer__rewards-fill');
      this._rewardsMarkers = this.querySelector('.cart-drawer__rewards-markers');
      this._savingsEl = this.querySelector('.cart-drawer__savings');
      this._savingsAmountEl = this.querySelector('.cart-drawer__savings-amount');
      this._shippingEl = this.querySelector('.cart-drawer__shipping');

      // Bind element-scoped event handlers
      this._handleClick = this._handleClick.bind(this);
      this._handleKeydown = this._handleKeydown.bind(this);

      // Panel click delegation (qty, remove, close)
      this._panel.addEventListener('click', this._handleClick);

      // Overlay close
      this._overlay.addEventListener('click', () => this.close());

      // Keyboard (ESC, focus trap) — scoped to this element
      document.addEventListener('keydown', this._handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._handleKeydown);
    }

    /* ------------------------------------------------------------------ */
    /*  Configuration                                                      */
    /* ------------------------------------------------------------------ */

    get config() {
      if (!this._config) {
        const el = document.getElementById('CartDrawerConfig');
        this._config = el ? JSON.parse(el.textContent) : {};
      }
      return this._config;
    }

    /* ------------------------------------------------------------------ */
    /*  Open / Close                                                       */
    /* ------------------------------------------------------------------ */

    async open() {
      if (this._isOpen) {
        // Already open — just refresh data
        await this._refreshCart();
        return;
      }

      this._triggerElement = document.activeElement;
      this._isOpen = true;

      this._lockBodyScroll();
      this.classList.add('cart-drawer--open');

      // Fetch and render
      await this._refreshCart();

      // Focus panel for keyboard nav
      this._panel.focus();
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;

      this.classList.remove('cart-drawer--open');
      this._unlockBodyScroll();

      // Restore focus
      if (this._triggerElement && typeof this._triggerElement.focus === 'function') {
        this._triggerElement.focus();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Body Scroll Lock                                                   */
    /* ------------------------------------------------------------------ */

    _lockBodyScroll() {
      // iOS Safari fix: use position fixed
      this._scrollPosition = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.setProperty('--cd-scrollbar-width', scrollbarWidth + 'px');
      document.body.classList.add('cart-drawer-open');
    }

    _unlockBodyScroll() {
      document.body.classList.remove('cart-drawer-open');
      document.body.style.removeProperty('--cd-scrollbar-width');
    }

    /* ------------------------------------------------------------------ */
    /*  Cart API                                                           */
    /* ------------------------------------------------------------------ */

    async _fetchCart() {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch cart');
      return res.json();
    }

    async _updateQuantity(key, quantity) {
      if (this._isUpdating) return;
      this._isUpdating = true;

      // Optimistic UI: dim the item
      const itemEl = this._itemsContainer.querySelector(`[data-key="${key}"]`);
      if (itemEl) itemEl.classList.add('cart-drawer__item--updating');

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!res.ok) throw new Error('Update failed');
        const cartData = await res.json();
        this._render(cartData);

        // Notify other components (Dawn header cart count, etc.)
        this._notifyCartUpdate();
      } catch (err) {
        // Re-fetch to get correct state
        await this._refreshCart();
      } finally {
        this._isUpdating = false;
      }
    }

    async _refreshCart() {
      try {
        const cartData = await this._fetchCart();
        this._render(cartData);
      } catch (err) {
        // Silently fail — cart data will be stale but drawer still works
      }
    }

    _notifyCartUpdate() {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer' }); } catch (e) {}
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Rendering                                                          */
    /* ------------------------------------------------------------------ */

    _render(cartData) {
      this._cartData = cartData;
      const items = cartData.items || [];
      const hasItems = items.length > 0;

      // Sync free gift (non-blocking, guarded by _isSyncingGift)
      this._syncFreeGift(cartData);

      // Exclude gift items from count and threshold calculations
      const nonGiftCount = items
        .filter(i => !this._isFreeGiftItem(i))
        .reduce((sum, i) => sum + i.quantity, 0);
      const subtotalExGift = this._getSubtotalExcludingGift(cartData);

      // Update header count (exclude gift)
      this._countEl.textContent = nonGiftCount;

      // Update header cart icon badge (Dawn's cart count)
      this._updateDawnCartCount(nonGiftCount);

      // Rewards bar uses subtotal excluding gift (prevents circular logic)
      this._renderRewardsBar(subtotalExGift);

      if (hasItems) {
        this._itemsContainer.innerHTML = items.map(item => this._renderLineItem(item)).join('');

        // "Add another" reorder section
        this._renderReorderSection(items);

        this._emptyState.hidden = true;
        this._footer.hidden = false;

        // Subtotal — include gift value in original, subtract for actual total
        const originalTotal = cartData.original_total_price || cartData.total_price;
        const finalTotal = cartData.total_price;
        const bundleDiscount = originalTotal - finalTotal;

        // Gift savings — gifts are priced but given free
        const giftSavings = items
          .filter(i => this._isFreeGiftItem(i))
          .reduce((sum, i) => sum + (i.final_line_price || 0), 0);
        const actualTotal = finalTotal - giftSavings;

        // Check free shipping milestone — only show when threshold actually reached
        const milestones = this.config.milestones || [];
        const shippingMilestone = milestones.find(m => m.icon === 'truck');
        const shippingThreshold = (shippingMilestone && shippingMilestone.amount > 0) ? shippingMilestone.amount : Infinity;
        const hasFreeShipping = subtotalExGift >= shippingThreshold;
        const shippingSavings = hasFreeShipping ? 500 : 0; // $5.00 shipping
        const totalSavings = bundleDiscount + giftSavings + shippingSavings;

        if (bundleDiscount > 0 || giftSavings > 0) {
          this._subtotalPrice.innerHTML =
            `<span class="cart-drawer__subtotal-original">${this._formatMoney(finalTotal)}</span>` +
            `<span class="cart-drawer__subtotal-sale">${this._formatMoney(actualTotal)}</span>`;
        } else {
          this._subtotalPrice.textContent = this._formatMoney(actualTotal);
        }

        // Savings callout — includes bundle discount + gift value + free shipping
        if (this._savingsEl && this._savingsAmountEl) {
          if (totalSavings > 0) {
            this._savingsAmountEl.textContent = this._formatMoney(totalSavings);
            this._savingsEl.hidden = false;
          } else {
            this._savingsEl.hidden = true;
          }
        }

        // Free shipping line
        if (this._shippingEl) {
          this._shippingEl.hidden = !hasFreeShipping;
        }
      } else {
        this._itemsContainer.innerHTML = '';
        // Remove reorder section if present
        const reorder = this._panel.querySelector('.cart-drawer__reorder');
        if (reorder) reorder.remove();
        if (this._savingsEl) this._savingsEl.hidden = true;
        if (this._shippingEl) this._shippingEl.hidden = true;
        this._emptyState.hidden = false;
        this._footer.hidden = true;
      }
    }

    _renderLineItem(item) {
      // Free gift item — special rendering (no qty controls, no remove button)
      if (this._isFreeGiftItem(item)) {
        return this._renderGiftLineItem(item);
      }

      const petName = item.properties?.['Pet Name'] || '';
      const eyeColor = item.properties?.['Eye Color'] || '';
      const petPhoto = item.properties?.['Pet Photo'] || '';

      let propsHtml = '';
      if (petName) propsHtml += `<span class="cart-drawer__item-prop">Pet: ${this._esc(petName)}</span>`;
      if (eyeColor) propsHtml += `<span class="cart-drawer__item-prop">Eyes: ${this._esc(eyeColor)}</span>`;

      let photoHtml = '';
      if (petPhoto && petPhoto.startsWith('http')) {
        photoHtml = `<img src="${this._esc(petPhoto)}" class="cart-drawer__item-pet-photo" alt="Pet photo" loading="lazy" width="28" height="28">`;
      }

      // Use the first available image
      const imgSrc = item.image
        ? item.image
        : (item.featured_image?.url || '');

      // Price: show original struck through if discounted
      const originalLine = item.original_line_price || item.line_price || item.final_line_price;
      const finalLine = item.final_line_price;
      const isDiscounted = originalLine > finalLine;

      const priceHtml = isDiscounted
        ? `<span class="cart-drawer__item-price-original">${this._formatMoney(originalLine)}</span>
           <span class="cart-drawer__item-price cart-drawer__item-price--sale">${this._formatMoney(finalLine)}</span>`
        : `<span class="cart-drawer__item-price">${this._formatMoney(finalLine)}</span>`;

      return `
        <div class="cart-drawer__item" data-key="${this._esc(item.key)}">
          <div class="cart-drawer__item-image">
            ${imgSrc ? `<img src="${this._esc(imgSrc)}" alt="${this._esc(item.product_title)}" loading="lazy">` : ''}
          </div>
          <div class="cart-drawer__item-details">
            <div class="cart-drawer__item-top">
              <div class="cart-drawer__item-title">${this._esc(item.product_title)}</div>
              <button class="cart-drawer__item-remove" data-action="remove" data-key="${this._esc(item.key)}" aria-label="Remove ${this._esc(item.product_title)}">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="cart-drawer__item-props">
              ${propsHtml}
              ${photoHtml}
            </div>
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__item-qty">
                <button class="cart-drawer__qty-btn" data-action="decrease" data-key="${this._esc(item.key)}" aria-label="Decrease quantity">&minus;</button>
                <span class="cart-drawer__qty-value">${item.quantity}</span>
                <button class="cart-drawer__qty-btn" data-action="increase" data-key="${this._esc(item.key)}" aria-label="Increase quantity">&plus;</button>
              </div>
              <div class="cart-drawer__item-prices">
                ${priceHtml}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    _renderGiftLineItem(item) {
      const imgSrc = item.image
        ? item.image
        : (item.featured_image?.url || '');
      const qty = item.quantity || 1;
      const linePrice = item.final_line_price || item.line_price || 0;

      return `
        <div class="cart-drawer__item cart-drawer__item--gift" data-key="${this._esc(item.key)}">
          <div class="cart-drawer__item-image">
            ${imgSrc ? `<img src="${this._esc(imgSrc)}" alt="${this._esc(item.product_title)}" loading="lazy">` : ''}
          </div>
          <div class="cart-drawer__item-details">
            <div class="cart-drawer__item-top">
              <div class="cart-drawer__item-title">${this._esc(item.product_title)}${qty > 1 ? ` x${qty}` : ''}</div>
            </div>
            <div class="cart-drawer__item-bottom">
              <span class="cart-drawer__gift-badge">🎁 Free Gift${qty > 1 ? ` (1 per kit)` : ''}</span>
              <span class="cart-drawer__item-price cart-drawer__item-price--free">
                ${linePrice > 0 ? `<s class="cart-drawer__item-price--original">${this._formatMoney(linePrice)}</s> ` : ''}FREE 🎁
              </span>
            </div>
          </div>
        </div>
      `;
    }

    _renderRewardsBar(totalPriceCents) {
      const milestones = this.config.milestones || [];
      if (!milestones.length) return;

      const maxAmount = milestones[milestones.length - 1].amount;
      const nextMilestone = milestones.find(m => totalPriceCents < m.amount);

      // Progress fill
      const progressPercent = maxAmount > 0
        ? Math.min(100, (totalPriceCents / maxAmount) * 100)
        : 0;
      this._rewardsFill.style.width = progressPercent + '%';

      // Copy text
      if (totalPriceCents === 0) {
        this._rewardsCopy.textContent = 'Add items to unlock rewards!';
        this._rewardsCopy.classList.remove('cart-drawer__rewards-copy--complete');
      } else if (nextMilestone) {
        const remaining = nextMilestone.amount - totalPriceCents;
        this._rewardsCopy.textContent = `Add ${this._formatMoney(remaining)} to get ${nextMilestone.label}!`;
        this._rewardsCopy.classList.remove('cart-drawer__rewards-copy--complete');
      } else {
        // All milestones achieved
        const labels = milestones.map(m => m.label).join(' + ');
        this._rewardsCopy.textContent = `You've unlocked ${labels}!`;
        this._rewardsCopy.classList.add('cart-drawer__rewards-copy--complete');
      }

      // Milestone markers with labels directly underneath
      this._rewardsMarkers.innerHTML = milestones.map(m => {
        const pos = maxAmount > 0 ? (m.amount / maxAmount) * 100 : 0;
        const achieved = totalPriceCents >= m.amount;
        const emoji = m.icon === 'gift' ? '\uD83C\uDF81' : '\uD83D\uDE97';
        return `
          <div class="cart-drawer__milestone ${achieved ? 'cart-drawer__milestone--achieved' : ''}"
               style="left: ${pos}%">
            <span class="cart-drawer__milestone-emoji">${emoji}</span>
            <span class="cart-drawer__milestone-label ${achieved ? 'cart-drawer__milestone-label--achieved' : ''}">
              ${this._esc(m.label)}
            </span>
          </div>
        `;
      }).join('');
    }

    /* ------------------------------------------------------------------ */
    /*  Reorder Section — "Add another Pet Name"                           */
    /* ------------------------------------------------------------------ */

    _renderReorderSection(items) {
      // Remove existing reorder section if present
      const existing = this._panel.querySelector('.cart-drawer__reorder');
      if (existing) existing.remove();

      // Filter items that have pet names, excluding gift items
      const petItems = items.filter(item => item.properties?.['Pet Name'] && !this._isFreeGiftItem(item));
      if (!petItems.length) return;

      // Calculate per-item savings info (exclude gift items from count)
      const nonGiftItems = items.filter(i => !this._isFreeGiftItem(i));
      const currentPetCount = nonGiftItems.reduce((sum, i) => sum + i.quantity, 0);
      const basePrice = this.config.basePrice || 5400;
      const currentDiscount = this._getDiscountCents(currentPetCount);
      const nextDiscount = this._getDiscountCents(currentPetCount + 1);
      const additionalSavings = nextDiscount - currentDiscount;

      // How many kits needed for next reward milestone?
      const milestones = this.config.milestones || [];
      const cartTotal = this._cartData?.total_price || 0;
      const nextReward = milestones.find(m => cartTotal < m.amount);
      let rewardHint = '';
      if (nextReward) {
        // Simulate adding kits until we pass the milestone
        let simTotal = cartTotal;
        let kitsNeeded = 0;
        let simCount = currentPetCount;
        while (simTotal < nextReward.amount && kitsNeeded < 20) {
          kitsNeeded++;
          simCount++;
          simTotal = this._getTotalForPetCount(simCount);
        }
        rewardHint = `+${kitsNeeded} Kit${kitsNeeded > 1 ? 's' : ''} for ${this._esc(nextReward.label)}`;
      }

      const reorderHtml = petItems.map(item => {
        const petName = item.properties['Pet Name'] || '';
        const petPhoto = item.properties['Pet Photo'] || '';
        const variantId = item.variant_id || item.id;

        const photoEl = (petPhoto && petPhoto.startsWith('http'))
          ? `<img src="${this._esc(petPhoto)}" class="cart-drawer__reorder-photo" alt="${this._esc(petName)}" loading="lazy" width="36" height="36">`
          : `<span class="cart-drawer__reorder-photo cart-drawer__reorder-photo--placeholder">🐾</span>`;

        return `
          <div class="cart-drawer__reorder-item">
            ${photoEl}
            <div class="cart-drawer__reorder-info">
              <span class="cart-drawer__reorder-name">${this._esc(petName)}</span>
              <span class="cart-drawer__reorder-pricing">
                <span class="cart-drawer__reorder-list">${this._formatMoney(basePrice)}</span>
                <span class="cart-drawer__reorder-save">+${this._formatMoney(additionalSavings)} in Bundle Savings</span>
              </span>
            </div>
            <button class="cart-drawer__reorder-btn"
                    data-action="reorder"
                    data-variant-id="${variantId}"
                    data-properties='${JSON.stringify(item.properties || {}).replace(/'/g, "&#39;")}'
                    aria-label="Add another ${this._esc(petName)}">
              + Add another
            </button>
          </div>
        `;
      }).join('');

      const section = document.createElement('div');
      section.className = 'cart-drawer__reorder';
      section.innerHTML = `
        <div class="cart-drawer__reorder-header">
          <span class="cart-drawer__reorder-title">Order More Kits</span>
          ${rewardHint ? `<span class="cart-drawer__reorder-reward">${rewardHint}</span>` : ''}
        </div>
        ${reorderHtml}
      `;

      // Insert after items, before empty state
      this._itemsContainer.after(section);
    }

    /* ------------------------------------------------------------------ */
    /*  Dawn Cart Count Badge Update                                       */
    /* ------------------------------------------------------------------ */

    _updateDawnCartCount(count) {
      // Dawn theme uses various selectors for the header cart count
      const selectors = [
        '.cart-count-bubble span[aria-hidden]',
        '.header__cart-count',
        '[data-cart-count]',
      ];
      selectors.forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.textContent = count;
        });
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Event Handling (element-scoped)                                    */
    /* ------------------------------------------------------------------ */

    _handleClick(e) {
      // Checkout button — validate pet photos before allowing checkout
      const checkoutBtn = e.target.closest('.cart-drawer__checkout');
      if (checkoutBtn) {
        if (this._hasPetItemsMissingPhoto()) {
          e.preventDefault();
          e.stopPropagation();
          this._showDrawerError('One or more pets are missing a photo. Please update your cart before checking out.');
          return;
        }
        return; // All good — let native form POST proceed
      }

      // Close button
      const closeBtn = e.target.closest('[data-close]');
      if (closeBtn) {
        this.close();
        return;
      }

      // Quantity / Remove / Reorder actions
      const actionBtn = e.target.closest('[data-action]');
      if (!actionBtn) return;

      const action = actionBtn.dataset.action;

      if (action === 'reorder') {
        this._handleReorder(actionBtn);
        return;
      }

      const key = actionBtn.dataset.key;
      if (!key) return;

      if (action === 'remove') {
        this._updateQuantity(key, 0);
      } else if (action === 'decrease') {
        const item = (this._cartData?.items || []).find(i => i.key === key);
        if (item && item.quantity > 1) {
          this._updateQuantity(key, item.quantity - 1);
        } else {
          this._updateQuantity(key, 0);
        }
      } else if (action === 'increase') {
        const item = (this._cartData?.items || []).find(i => i.key === key);
        if (item) {
          this._updateQuantity(key, item.quantity + 1);
        }
      }
    }

    async _handleReorder(btn) {
      if (this._isUpdating) return;
      this._isUpdating = true;

      const variantId = btn.dataset.variantId;
      let properties = {};
      try { properties = JSON.parse(btn.dataset.properties || '{}'); } catch (e) {}

      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1,
              properties: properties,
            }],
          }),
        });

        if (!res.ok) throw new Error('Add failed');
        await this._refreshCart();
        this._notifyCartUpdate();
      } catch (err) {
        await this._refreshCart();
      } finally {
        this._isUpdating = false;
      }
    }

    _handleKeydown(e) {
      if (e.key === 'Escape' && this._isOpen) {
        e.preventDefault();
        this.close();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && this._isOpen) {
        this._trapFocus(e);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Focus Trap                                                         */
    /* ------------------------------------------------------------------ */

    _trapFocus(e) {
      const focusable = this._panel.querySelectorAll(
        'a[href], button:not([disabled]):not([hidden]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Free Gift Helpers                                                   */
    /* ------------------------------------------------------------------ */

    _isFreeGiftItem(item) {
      return !!(item.properties && item.properties['_free_gift'] === 'true');
    }

    _getSubtotalExcludingGift(cartData) {
      const items = cartData.items || [];
      const giftTotal = items
        .filter(item => this._isFreeGiftItem(item))
        .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
      return (cartData.total_price || 0) - giftTotal;
    }

    _countPetKits(items) {
      const kitIds = (this.config.petKitVariantIds || []).map(Number);
      return items
        .filter(i => kitIds.includes(i.variant_id) && !this._isFreeGiftItem(i))
        .reduce((sum, i) => sum + i.quantity, 0);
    }

    async _syncFreeGift(cartData) {
      const giftVariantId = this.config.freeGiftVariantId;
      if (!giftVariantId || this._isSyncingGift) return;

      const items = cartData.items || [];
      const giftInCart = items.find(i => this._isFreeGiftItem(i));
      const desiredQty = this._countPetKits(items);
      const currentQty = giftInCart ? giftInCart.quantity : 0;

      // Already correct — nothing to do
      if (desiredQty === currentQty) return;

      this._isSyncingGift = true;
      try {
        if (desiredQty > 0 && !giftInCart) {
          // Add gift (no gift in cart yet)
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(giftVariantId, 10), quantity: desiredQty, properties: { '_free_gift': 'true' } }],
            }),
          });
          if (!res.ok) {
            console.warn('[CartDrawer] Failed to add free gift:', await res.text());
            this._isSyncingGift = false;
            return;
          }
        } else if (desiredQty > 0 && giftInCart) {
          // Update gift quantity
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: giftInCart.key, quantity: desiredQty }),
          });
          if (!res.ok) {
            console.warn('[CartDrawer] Failed to update free gift qty:', await res.text());
            this._isSyncingGift = false;
            return;
          }
        } else {
          // Remove gift (no pet kits in cart)
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: giftInCart.key, quantity: 0 }),
          });
          if (!res.ok) {
            console.warn('[CartDrawer] Failed to remove free gift:', await res.text());
            this._isSyncingGift = false;
            return;
          }
        }

        const newCart = await this._fetchCart();
        this._isSyncingGift = false;
        this._render(newCart);
        this._notifyCartUpdate();
      } catch (e) {
        console.warn('[CartDrawer] Free gift sync error:', e);
        this._isSyncingGift = false;
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Bundle Discount Helpers                                             */
    /*  Mirrors pet-bundle-builder.js _getDiscountAmountCents()             */
    /* ------------------------------------------------------------------ */

    _getDiscountCents(totalPets) {
      const tiers = this.config.discountTiers || {};
      const perPet = this.config.discountPerPetAbove5 || 1100;
      if (totalPets > 5) return (tiers['5'] || 0) + ((totalPets - 5) * perPet);
      return tiers[String(totalPets)] || 0;
    }

    _getTotalForPetCount(n) {
      const base = this.config.basePrice || 5400;
      return (base * n) - this._getDiscountCents(n);
    }

    _getIncrementalCost(currentPetCount) {
      const currentTotal = this._getTotalForPetCount(currentPetCount);
      const newTotal = this._getTotalForPetCount(currentPetCount + 1);
      return newTotal - currentTotal;
    }

    /* ------------------------------------------------------------------ */
    /*  Money Formatting                                                   */
    /*  Ported from pet-bundle-builder.js _formatMoney()                   */
    /* ------------------------------------------------------------------ */

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const noDecimals = Math.round(cents / 100).toString();
      const withComma = amount.replace('.', ',');
      let fmt = this.config.moneyFormat || '${{amount}}';
      return fmt
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    /* ------------------------------------------------------------------ */
    /*  Checkout Validation                                                */
    /* ------------------------------------------------------------------ */

    _hasPetItemsMissingPhoto() {
      if (!this._cartData || !this._cartData.items) return false;
      return this._cartData.items.some(item => {
        const props = item.properties || {};
        const isPetItem = props['Pet Name'] || props['Eye Color'];
        const hasPhoto = props['Pet Photo'] && props['Pet Photo'].startsWith('http');
        return isPetItem && !hasPhoto;
      });
    }

    _showDrawerError(msg) {
      let errEl = this.querySelector('.cart-drawer__checkout-error');
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'cart-drawer__checkout-error';
        errEl.style.cssText = 'color:#c00;font-size:13px;padding:8px 16px;text-align:center;';
        const footer = this.querySelector('.cart-drawer__footer');
        if (footer) footer.prepend(errEl);
        else return;
      }
      errEl.textContent = msg;
      errEl.style.display = '';
      setTimeout(() => { errEl.style.display = 'none'; }, 8000);
    }

    /* ------------------------------------------------------------------ */
    /*  Utility                                                            */
    /* ------------------------------------------------------------------ */

    _esc(str) {
      const el = document.createElement('span');
      el.textContent = str || '';
      return el.innerHTML;
    }
  }

  customElements.define('cart-drawer-custom', CartDrawer);
}

/* --------------------------------------------------------------------------
   Module-level event listeners
   —————————————————————————————
   Registered once when the script executes. These do NOT depend on the
   custom-element lifecycle (connectedCallback / disconnectedCallback),
   which can silently detach listeners during DOM moves or upgrades.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  function getDrawer() {
    return document.querySelector('cart-drawer-custom');
  }

  /* ── cart:open — open the drawer ── */
  document.addEventListener('cart:open', function () {
    var el = getDrawer();
    if (el && typeof el.open === 'function') {
      el._skipNextUpdate = true;
      el.open();
    }
  });

  /* ── cart:updated — refresh if already open ── */
  document.addEventListener('cart:updated', function () {
    var el = getDrawer();
    if (!el) return;
    if (el._isOpen && !el._skipNextUpdate) {
      el._refreshCart();
    }
    el._skipNextUpdate = false;
  });

  /* ── Header cart link interception ── */
  document.addEventListener('click', function (e) {
    var link = e.target.closest(
      'a[href="/cart"], a[href$="/cart"], a[href*="/cart"], .header__icon--cart, #cart-icon-bubble, [data-cart-link]'
    );
    if (link && !link.closest('cart-drawer-custom')) {
      e.preventDefault();
      e.stopPropagation();
      var el = getDrawer();
      if (el && typeof el.open === 'function') el.open();
    }
  }, true); // capture phase — intercept before Dawn handlers

  /* ── Dawn pub/sub integration (optional) ── */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'cart-drawer') return;
        var el = getDrawer();
        if (!el) return;
        if (el._isOpen && !el._skipNextUpdate) {
          el._refreshCart();
        }
        el._skipNextUpdate = false;
      });
    } catch (e) { /* pub/sub not available */ }
  }
})();
